import { useTranslation } from "react-i18next";import React, { useState, useEffect, useRef } from 'react';
import {
  Truck, Plus, Search, Filter, MoreVertical, MapPin, Fuel,
  Gauge, Calendar, Edit, Trash2, Eye, RefreshCw, Settings,
  CheckCircle, XCircle, Wrench, ChevronDown, X, Upload, Image,
  Clock, Navigation, Phone, MessageSquare, FileText, Building2 } from
'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import { useVehicles, useVehicleTypes, useFleetAPI } from '../hooks/useFleetData';
import {
  VehicleIllustrationCard,
  getVehicleIllustration,
  VEHICLE_ILLUSTRATIONS,
  CargoVanSVG,
  BoxTruckSVG,
  SemiTruckSVG,
  PickupTruckSVG,
  SprinterVanSVG,
  MinivanSVG,
  RefrigeratedTruckSVG,
  FlatbedTruckSVG,
  MotorcycleSVG,
  TowTruckSVG } from
'../components/VehicleIllustrations';


// Helper to normalize status to lowercase for lookup
const normalizeStatus = (status) => {
  return status?.toString()?.toLowerCase() || 'available';
};

const statusConfig = {
  available: { color: 'bg-emerald-500', textColor: 'text-emerald-600', bgLight: 'bg-emerald-50', label: 'Available', icon: CheckCircle },
  on_route: { color: 'bg-green-500', textColor: 'text-green-600', bgLight: 'bg-green-50', label: 'On Route', icon: Navigation },
  loading: { color: 'bg-amber-500', textColor: 'text-amber-600', bgLight: 'bg-amber-50', label: 'Loading', icon: Truck },
  unloading: { color: 'bg-amber-500', textColor: 'text-amber-600', bgLight: 'bg-amber-50', label: 'Unloading', icon: Truck },
  maintenance: { color: 'bg-orange-500', textColor: 'text-orange-600', bgLight: 'bg-orange-50', label: 'Maintenance', icon: Wrench },
  offline: { color: 'bg-gray-400', textColor: 'text-gray-600', bgLight: 'bg-gray-50', label: 'Offline', icon: XCircle },
  out_of_service: { color: 'bg-red-500', textColor: 'text-red-600', bgLight: 'bg-red-50', label: 'Out of Service', icon: XCircle },
  reserved: { color: 'bg-purple-500', textColor: 'text-purple-600', bgLight: 'bg-purple-50', label: 'Reserved', icon: Calendar },
  waiting: { color: 'bg-yellow-500', textColor: 'text-yellow-600', bgLight: 'bg-yellow-50', label: 'Waiting', icon: Clock }
};

const ownershipLabels = {
  company_owned: 'Owned',
  company_leased: 'Leased',
  partner: 'Partner',
  client_owned: 'Client',
  client_partner: 'Client Partner',
  contractor: 'Contractor',
  // Uppercase versions
  COMPANY_OWNED: 'Owned',
  COMPANY_LEASED: 'Leased',
  PARTNER: 'Partner',
  CLIENT_OWNED: 'Client',
  CLIENT_PARTNER: 'Client Partner',
  CONTRACTOR: 'Contractor'
};

const VehiclesPage = () => {const { t } = useTranslation("fleet");
  const [filters, setFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleToEdit, setVehicleToEdit] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // all, active, inactive

  const { vehicles, loading, refresh } = useVehicles(filters);
  const vehicleTypes = useVehicleTypes();
  const { fetchAPI } = useFleetAPI();

  // Filter vehicles by search term and tab
  const filteredVehicles = vehicles.filter((v) => {
    const matchesSearch =
    v.vehicle_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.license_plate?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.make?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.model?.toLowerCase().includes(searchTerm.toLowerCase());

    const normalizedStatus = normalizeStatus(v.status);
    if (activeTab === 'active') {
      return matchesSearch && ['available', 'on_route', 'loading', 'unloading'].includes(normalizedStatus);
    } else if (activeTab === 'inactive') {
      return matchesSearch && ['offline', 'maintenance', 'out_of_service'].includes(normalizedStatus);
    }
    return matchesSearch;
  });

  // Group by status for stats
  const statusCounts = vehicles.reduce((acc, v) => {
    const normalized = normalizeStatus(v.status);
    acc[normalized] = (acc[normalized] || 0) + 1;
    return acc;
  }, {});

  const activeCount = vehicles.filter((v) => ['available', 'on_route', 'loading', 'unloading'].includes(normalizeStatus(v.status))).length;
  const inactiveCount = vehicles.filter((v) => ['offline', 'maintenance', 'out_of_service'].includes(normalizeStatus(v.status))).length;

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="vehicles-page-title">{t("tracking", "Tracking")}</h1>
          <p className="text-gray-500">{t("fleetVehiclesOverview", "Fleet vehicles overview and management")}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search vehicles..."
              className="pl-9 w-64 bg-white"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="vehicles-search-input" />
            
          </div>
          <Button variant="outline" size="sm" onClick={refresh} data-testid="refresh-vehicles-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button
            className="bg-red-500 hover:bg-red-600 text-white"
            size="sm"
            onClick={() => setShowAddModal(true)}
            data-testid="add-vehicle-btn">
            
            <Plus className="w-4 h-4 mr-2" />{t("addVehicle", "Add Vehicle")}

          </Button>
        </div>
      </div>

      {/* Filter Pills - Like Reference Design */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-sm text-gray-500 mr-2">{t("filterByPartners", "Filter by Partners")}</span>
        {['ShipHike', 'Roambee', 'Post Hawk', 'Loginext', 'Forwardo', 'Lopez Pallets'].map((partner, idx) =>
        <button
          key={partner}
          className="px-3 py-1.5 rounded-full text-sm font-medium border border-gray-200 bg-white hover:bg-gray-50 transition-colors flex items-center gap-1">
          
            <span className={`w-2 h-2 rounded-full ${idx % 2 === 0 ? 'bg-red-400' : 'bg-green-400'}`}></span>
            {partner}
            <span className="text-xs text-gray-400 ml-1">{Math.floor(Math.random() * 20) + 5}</span>
          </button>
        )}
      </div>

      {/* Status Tabs - Like Reference */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-gray-500">{t("show", "Show")}</span>
        <button
          onClick={() => setActiveTab('active')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${activeTab === 'active' ?
          'bg-green-500 text-white' :
          'bg-green-100 text-green-700 hover:bg-green-200'}`
          }
          data-testid="filter-active-tab">{t("active", "Active")}

          <span className="ml-1 font-bold">{activeCount}</span>
        </button>
        <button
          onClick={() => setActiveTab('inactive')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${activeTab === 'inactive' ?
          'bg-red-500 text-white' :
          'bg-red-100 text-red-700 hover:bg-red-200'}`
          }
          data-testid="filter-inactive-tab">{t("inactive", "Inactive")}

          <span className="ml-1 font-bold">{inactiveCount}</span>
        </button>
        <button
          onClick={() => setActiveTab('all')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${activeTab === 'all' ?
          'bg-gray-700 text-white' :
          'bg-gray-100 text-gray-700 hover:bg-gray-200'}`
          }
          data-testid="filter-all-tab">{t("all", "All")}

          <span className="ml-1 font-bold">{vehicles.length}</span>
        </button>
      </div>

      {/* Vehicle Cards Grid - Matching Reference Design */}
      {loading ?
      <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-red-500" />
        </div> :
      filteredVehicles.length === 0 ?
      <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-12 text-center">
            <div className="w-32 h-24 mx-auto mb-4">
              <BoxTruckSVG className="w-full h-full opacity-30" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t("noVehiclesFound", "No vehicles found")}</h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || Object.keys(filters).length > 0 ?
            'Try adjusting your search or filters' :
            'Get started by adding your first vehicle'
            }
            </p>
            <Button
            className="bg-red-500 hover:bg-red-600 text-white"
            onClick={() => setShowAddModal(true)}
            data-testid="add-first-vehicle-btn">
            
              <Plus className="w-4 h-4 mr-2" />{t("addVehicle", "Add Vehicle")}

          </Button>
          </CardContent>
        </Card> :

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filteredVehicles.map((vehicle) =>
        <VehicleVisualCard
          key={vehicle.id}
          vehicle={vehicle}
          onClick={() => setSelectedVehicle(vehicle)} />

        )}
        </div>
      }

      {/* Vehicle Modal - Create/Edit */}
      {(showAddModal || showEditModal) &&
      <VehicleModal
        mode={showEditModal ? 'edit' : 'create'}
        vehicle={vehicleToEdit}
        vehicleTypes={vehicleTypes}
        onClose={() => {
          setShowAddModal(false);
          setShowEditModal(false);
          setVehicleToEdit(null);
        }}
        onSuccess={() => {
          setShowAddModal(false);
          setShowEditModal(false);
          setVehicleToEdit(null);
          refresh();
        }} />

      }

      {/* Vehicle Detail Panel */}
      {selectedVehicle &&
      <VehicleDetailPanel
        vehicle={selectedVehicle}
        onClose={() => setSelectedVehicle(null)}
        onEdit={(v) => {
          setVehicleToEdit(v);
          setShowEditModal(true);
        }} />

      }
    </div>);

};

// Visual Vehicle Card - Matching Reference Design
const VehicleVisualCard = ({ vehicle, onClick }) => {const { t } = useTranslation("fleet");
  const normalizedStatus = normalizeStatus(vehicle.status);
  const status = statusConfig[normalizedStatus] || { color: 'bg-gray-500', label: vehicle.status, textColor: 'text-gray-600' };
  const VehicleIllustration = getVehicleIllustration(vehicle.vehicle_type);

  // Calculate mock route stops for demo
  const routeStops = vehicle.route_stops || [
  { name: 'Warehouse A', time: '08:30' },
  { name: 'Customer B', time: '09:15' },
  { name: 'Customer C', time: '10:00' },
  { name: 'Depot', time: '11:30' }];


  // Random ETA for demo
  const etaHours = Math.floor(Math.random() * 3);
  const etaMinutes = Math.floor(Math.random() * 60);
  const etaDisplay = `${String(etaHours).padStart(2, '0')}:${String(etaMinutes).padStart(2, '0')}:${String(Math.floor(Math.random() * 60)).padStart(2, '0')}`;
  const minsLeft = etaHours * 60 + etaMinutes;

  return (
    <Card
      className={`bg-white border-2 shadow-sm hover:shadow-lg transition-all cursor-pointer group overflow-hidden ${vehicle.status === 'on_route' ? 'border-red-200' : 'border-transparent hover:border-gray-200'}`
      }
      onClick={onClick}
      data-testid={`vehicle-card-${vehicle.vehicle_code}`}>
      
      <CardContent className="p-0">
        {/* Header with ID and Status */}
        <div className="p-4 pb-2 flex items-start justify-between">
          <div>
            <h3 className="font-bold text-gray-900 text-lg" data-testid="vehicle-code">{vehicle.vehicle_code}</h3>
          </div>
          <Badge
            className={`${status.color} text-white text-xs px-2 py-1 rounded-full flex items-center gap-1`}
            data-testid="vehicle-status-badge">
            
            <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
            {status.label}
          </Badge>
        </div>

        {/* ETA and Time Info */}
        <div className="px-4 pb-2 flex items-center gap-4">
          <div>
            <span className="text-2xl font-bold text-gray-900">{etaDisplay}</span>
            <p className="text-xs text-gray-500">{minsLeft}{t("minLeft", "min. left")}</p>
          </div>
        </div>

        {/* Route Stops Preview */}
        <div className="px-4 pb-2">
          <div className="flex items-start gap-2">
            <div className="flex flex-col items-center">
              {routeStops.slice(0, 4).map((_, idx) =>
              <React.Fragment key={idx}>
                  <div className={`w-2 h-2 rounded-full ${idx === 0 ? 'bg-red-500' : 'bg-gray-300'}`}></div>
                  {idx < 3 && <div className="w-0.5 h-4 bg-gray-200"></div>}
                </React.Fragment>
              )}
            </div>
            <div className="flex-1 text-xs text-gray-600 space-y-1">
              {routeStops.slice(0, 4).map((stop, idx) =>
              <p key={idx} className={idx === 0 ? 'font-medium text-gray-900' : ''}>
                  {stop.name}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Vehicle Illustration */}
        <div className="px-4 py-3 bg-gray-50 border-t">
          {vehicle.custom_image ?
          <img
            src={vehicle.custom_image}
            alt={vehicle.vehicle_code}
            className="w-full h-24 object-contain"
            data-testid="vehicle-custom-image" /> :


          <VehicleIllustration
            className="w-full h-24"
            fillColor="#ffffff"
            trailerColor="#ef4444"
            capacityPercent={vehicle.capacity_percent || Math.floor(Math.random() * 40) + 60} />

          }
        </div>
      </CardContent>
    </Card>);

};

// Reusable Vehicle Modal for Add & Edit
const VehicleModal = ({ mode = 'create', vehicle = null, vehicleTypes, onClose, onSuccess }) => {const { t } = useTranslation("fleet");
  const [formData, setFormData] = useState({
    vehicle_type: vehicle?.vehicle_type || 'van',
    ownership_type: vehicle?.ownership_type || 'company_owned',
    make: vehicle?.make || '',
    model: vehicle?.model || '',
    year: vehicle?.year || new Date().getFullYear(),
    license_plate: vehicle?.license_plate || '',
    fuel_type: vehicle?.fuel_type || 'diesel',
    max_load_kg: vehicle?.max_load_kg || '',
    has_gps: vehicle?.has_gps ?? true,
    has_refrigeration: vehicle?.has_refrigeration ?? false,
    custom_image: vehicle?.custom_image || null,
    vehicle_code: vehicle?.vehicle_code || ''
  });

  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(vehicle?.custom_image || null);
  const [useCustomImage, setUseCustomImage] = useState(!!vehicle?.custom_image);
  const fileInputRef = useRef(null);
  const { fetchAPI } = useFleetAPI();

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setFormData((f) => ({ ...f, custom_image: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const url = mode === 'create' ? '/vehicles' : `/vehicles/${vehicle.id}`;
      const method = mode === 'create' ? 'POST' : 'PUT';

      await fetchAPI(url, {
        method,
        body: JSON.stringify({
          ...formData,
          max_load_kg: formData.max_load_kg ? parseFloat(formData.max_load_kg) : null
        })
      });
      onSuccess();
    } catch (err) {
      console.error(`Failed to ${mode} vehicle:`, err);
    } finally {
      setLoading(false);
    }
  };

  // Preview SVG based on selected type
  const PreviewIllustration = getVehicleIllustration(formData.vehicle_type);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <Card className="w-full max-w-4xl m-4 bg-white max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <CardHeader className="border-b sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-red-500" />
              {mode === 'create' ? 'Add New Vehicle' : `Edit Vehicle: ${vehicle?.vehicle_code}`}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="close-vehicle-modal">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Image Selection Section */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Image className="w-5 h-5" />{t("vehicleImage", "Vehicle Image")}

              </h3>

              <div className="flex items-center gap-4 mb-4">
                <button
                  type="button"
                  onClick={() => setUseCustomImage(false)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${!useCustomImage ?
                  'bg-red-500 text-white' :
                  'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'}`
                  }
                  data-testid="use-illustration-btn">{t("useIllustration", "Use Illustration")}


                </button>
                <button
                  type="button"
                  onClick={() => setUseCustomImage(true)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${useCustomImage ?
                  'bg-red-500 text-white' :
                  'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'}`
                  }
                  data-testid="use-custom-image-btn">{t("uploadCustomPhoto", "Upload Custom Photo")}


                </button>
              </div>

              {!useCustomImage ? (
              /* Illustration Selection Grid */
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                { type: 'van', name: 'Cargo Van', Component: CargoVanSVG },
                { type: 'box_truck', name: 'Box Truck', Component: BoxTruckSVG },
                { type: 'semi_truck', name: 'Semi Truck', Component: SemiTruckSVG },
                { type: 'pickup', name: 'Pickup', Component: PickupTruckSVG },
                { type: 'sprinter', name: 'Sprinter Van', Component: SprinterVanSVG },
                { type: 'minivan', name: 'Minivan', Component: MinivanSVG },
                { type: 'refrigerated_truck', name: 'Refrigerated', Component: RefrigeratedTruckSVG },
                { type: 'flatbed', name: 'Flatbed', Component: FlatbedTruckSVG },
                { type: 'motorcycle', name: 'Motorcycle', Component: MotorcycleSVG },
                { type: 'tow_truck', name: 'Tow Truck', Component: TowTruckSVG }].
                map(({ type, name, Component }) =>
                <button
                  key={type}
                  type="button"
                  onClick={() => {
                    setFormData((f) => ({ ...f, vehicle_type: type, custom_image: null }));
                    setImagePreview(null);
                  }}
                  className={`p-4 rounded-xl border-2 transition-all ${formData.vehicle_type === type && !useCustomImage ?
                  'border-red-500 bg-red-50' :
                  'border-gray-200 bg-white hover:border-gray-300'}`
                  }
                  data-testid={`select-vehicle-type-${type}`}>
                  
                      <Component className="w-full h-16" fillColor="#ffffff" />
                      <p className="text-xs text-center mt-2 font-medium text-gray-700">{name}</p>
                    </button>
                )}
                </div>) : (

              /* Custom Image Upload */
              <div className="flex flex-col items-center">
                  {imagePreview ?
                <div className="relative">
                      <img
                    src={imagePreview}
                    alt="Vehicle preview"
                    className="max-w-md max-h-48 object-contain rounded-lg border" />
                  
                      <button
                    type="button"
                    onClick={() => {
                      setImagePreview(null);
                      setFormData((f) => ({ ...f, custom_image: null }));
                    }}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center">
                    
                        <X className="w-4 h-4" />
                      </button>
                    </div> :

                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full max-w-md h-48 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-red-400 hover:bg-red-50/50 transition-colors"
                  data-testid="upload-image-dropzone">
                  
                      <Upload className="w-10 h-10 text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600 font-medium">{t("clickToUpload", "Click to upload vehicle photo")}</p>
                      <p className="text-xs text-gray-400 mt-1">{t("pngJpgUp", "PNG, JPG up to 5MB")}</p>
                    </div>
                }
                  <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  data-testid="vehicle-image-input" />
                
                </div>)
              }
            </div>

            {/* Vehicle Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">{t("vehicleType", "Vehicle Type *")}</label>
                <select
                  className="w-full border rounded-lg p-2.5"
                  value={formData.vehicle_type}
                  onChange={(e) => setFormData((f) => ({ ...f, vehicle_type: e.target.value }))}
                  required
                  data-testid="vehicle-type-select">
                  
                  {vehicleTypes.map((t) =>
                  <option key={t.value} value={t.value}>{t.label}</option>
                  )}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">{t("ownership", "Ownership *")}</label>
                <select
                  className="w-full border rounded-lg p-2.5"
                  value={formData.ownership_type}
                  onChange={(e) => setFormData((f) => ({ ...f, ownership_type: e.target.value }))}
                  required
                  data-testid="ownership-type-select">
                  
                  {Object.entries(ownershipLabels).map(([value, label]) =>
                  <option key={value} value={value}>{label}</option>
                  )}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">{t("make", "Make")}</label>
                <Input
                  value={formData.make}
                  onChange={(e) => setFormData((f) => ({ ...f, make: e.target.value }))}
                  placeholder="e.g., Mercedes"
                  data-testid="vehicle-make-input" />
                
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">{t("model", "Model")}</label>
                <Input
                  value={formData.model}
                  onChange={(e) => setFormData((f) => ({ ...f, model: e.target.value }))}
                  placeholder="e.g., Sprinter"
                  data-testid="vehicle-model-input" />
                
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">{t("year", "Year")}</label>
                <Input
                  type="number"
                  value={formData.year}
                  onChange={(e) => setFormData((f) => ({ ...f, year: parseInt(e.target.value) }))}
                  data-testid="vehicle-year-input" />
                
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">{t("licensePlate", "License Plate")}</label>
                <Input
                  value={formData.license_plate}
                  onChange={(e) => setFormData((f) => ({ ...f, license_plate: e.target.value.toUpperCase() }))}
                  placeholder="e.g., ABC-1234"
                  data-testid="license-plate-input" />
                
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">{t("maxLoadKg", "Max Load (kg)")}</label>
                <Input
                  type="number"
                  value={formData.max_load_kg}
                  onChange={(e) => setFormData((f) => ({ ...f, max_load_kg: e.target.value }))}
                  placeholder="e.g., 1500"
                  data-testid="max-load-input" />
                
              </div>
            </div>

            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.has_gps}
                  onChange={(e) => setFormData((f) => ({ ...f, has_gps: e.target.checked }))}
                  className="w-4 h-4 rounded border-gray-300 text-red-500 focus:ring-red-500"
                  data-testid="has-gps-checkbox" />
                
                <span className="text-sm text-gray-700">{t("gpsEnabled", "GPS Enabled")}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.has_refrigeration}
                  onChange={(e) => setFormData((f) => ({ ...f, has_refrigeration: e.target.checked }))}
                  className="w-4 h-4 rounded border-gray-300 text-red-500 focus:ring-red-500"
                  data-testid="has-refrigeration-checkbox" />
                
                <span className="text-sm text-gray-700">{t("refrigerated", "Refrigerated")}</span>
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={onClose} data-testid="cancel-add-vehicle">{t("cancel", "Cancel")}

              </Button>
              <Button
                type="submit"
                className="bg-red-500 hover:bg-red-600 text-white"
                disabled={loading}
                data-testid="submit-add-vehicle">
                
                {loading ?
                <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />{t("creating", "Creating...")}

                </> :

                <>
                    <Plus className="w-4 h-4 mr-2" />
                    {mode === 'create' ? 'Add Vehicle' : 'Save Changes'}
                  </>
                }
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>);

};

// Vehicle Detail Panel - Matching Reference Right Panel Design
const VehicleDetailPanel = ({ vehicle, onClose, onEdit }) => {const { t } = useTranslation("fleet");
  const normalizedStatus = normalizeStatus(vehicle.status);
  const status = statusConfig[normalizedStatus] || { color: 'bg-gray-500', label: vehicle.status };
  const VehicleIllustration = getVehicleIllustration(vehicle.vehicle_type);

  // Mock data for demo
  const capacityPercent = vehicle.capacity_percent || Math.floor(Math.random() * 40) + 60;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-end z-50" onClick={onClose}>
      <div
        className="h-full w-full max-w-2xl bg-white shadow-2xl overflow-auto animate-slide-in-right"
        onClick={(e) => e.stopPropagation()}
        data-testid="vehicle-detail-panel">
        
        {/* Header */}
        <div className="sticky top-0 bg-white border-b z-10 p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-gray-900" data-testid="vehicle-detail-code">{vehicle.vehicle_code}</h2>
              <Badge className={`${status.color} text-white`}>
                <span className="w-1.5 h-1.5 bg-white rounded-full mr-1 animate-pulse"></span>
                {status.label}
              </Badge>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="close-detail-panel">
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Quick Actions */}
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="text-red-500 border-red-200 hover:bg-red-50">
              <Phone className="w-4 h-4 mr-1" />{t("callDriver", "Call Driver")}

            </Button>
            <Button variant="outline" size="sm" className="text-green-600 border-green-200 hover:bg-green-50">
              <MessageSquare className="w-4 h-4 mr-1" />{t("chatWithDriver", "Chat with Driver")}

            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b">
          <div className="flex">
            {['Shipping Info', 'Vehicle Info', 'Documents', 'Company', 'Billing'].map((tab, idx) =>
            <button
              key={tab}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${idx === 0 ?
              'border-red-500 text-red-600' :
              'border-transparent text-gray-500 hover:text-gray-700'}`
              }>
              
                {tab}
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Current Truck Capacity Section */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t("currentTruckCapacity", "Current Truck Capacity")}</h3>
            <div className="bg-gray-50 rounded-xl p-4 flex items-center gap-6">
              {/* Vehicle with capacity overlay */}
              <div className="relative flex-1">
                {vehicle.custom_image ?
                <div className="relative">
                    <img
                    src={vehicle.custom_image}
                    alt={vehicle.vehicle_code}
                    className="w-full max-h-32 object-contain" />
                  
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="bg-red-500/90 text-white text-3xl font-bold px-4 py-2 rounded-lg">
                        {capacityPercent}%
                      </div>
                    </div>
                  </div> :

                <SemiTruckSVG
                  className="w-full h-32"
                  capacityPercent={capacityPercent}
                  trailerColor="#ef4444" />

                }
              </div>
            </div>
          </div>

          {/* Vehicle Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500 mb-1">{vehicle.vehicle_code}</p>
              <p className="font-semibold">{vehicle.make} {vehicle.model}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500 mb-1">{t("licensePlate", "License Plate")}</p>
              <p className="font-semibold">{vehicle.license_plate || 'N/A'}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500 mb-1">{t("maxLoad", "Max Load")}</p>
              <p className="font-semibold">{vehicle.max_load_kg ? `${vehicle.max_load_kg} kg` : 'N/A'}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500 mb-1">{t("ownership", "Ownership")}</p>
              <p className="font-semibold">{ownershipLabels[vehicle.ownership_type] || vehicle.ownership_type}</p>
            </div>
          </div>

          {/* Route Info */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{t("route", "Route")}</h3>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-500">{t("013847", "01:38:47")}</span>
                <span className="text-gray-400">{t("57MinLeft", "57 min. left")}</span>
                <Button variant="outline" size="sm" className="text-red-500 border-red-200">
                  <Navigation className="w-4 h-4 mr-1" />{t("changeRoute", "Change Route")}

                </Button>
              </div>
            </div>

            {/* Map Placeholder */}
            <div className="bg-gray-100 rounded-xl h-48 flex items-center justify-center relative overflow-hidden">
              <div className="absolute inset-0 opacity-50">
                <svg className="w-full h-full" viewBox="0 0 400 200">
                  <path d="M50 150 Q100 100 150 120 Q200 140 250 80 Q300 20 350 50"
                  stroke="#ef4444" strokeWidth="3" fill="none" strokeDasharray="5,5" />
                  <circle cx="50" cy="150" r="8" fill="#ef4444" />
                  <circle cx="350" cy="50" r="8" fill="#22c55e" />
                </svg>
              </div>
              <span className="text-gray-400 text-sm z-10">{t("mapView", "Map View")}</span>
            </div>
          </div>

          {/* Driver Info */}
          {vehicle.current_driver &&
          <div className="bg-blue-50 rounded-xl p-4">
              <h3 className="text-sm font-medium text-blue-800 mb-2">{t("currentDriver", "Current Driver")}</h3>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-200 rounded-full flex items-center justify-center">
                  <span className="text-blue-800 font-bold text-lg">
                    {vehicle.current_driver.name?.charAt(0) || 'D'}
                  </span>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{vehicle.current_driver.name}</p>
                  <p className="text-sm text-gray-500">{t("driverId", "Driver ID:")}{vehicle.current_driver.id}</p>
                </div>
              </div>
            </div>
          }

          {/* Features */}
          <div className="flex items-center gap-4 pt-4 border-t">
            {vehicle.has_gps &&
            <div className="flex items-center gap-2 text-sm bg-emerald-50 text-emerald-700 px-3 py-2 rounded-lg">
                <MapPin className="w-4 h-4" />{t("gpsEnabled", "GPS Enabled")}

            </div>
            }
            {vehicle.has_refrigeration &&
            <div className="flex items-center gap-2 text-sm bg-blue-50 text-blue-700 px-3 py-2 rounded-lg">
                <span>{t("key_1990", "\u2744\uFE0F")}</span>{t("refrigerated", "Refrigerated")}

            </div>
            }
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => onEdit(vehicle)} data-testid="edit-vehicle-btn">
              <Edit className="w-4 h-4 mr-2" />{t("editVehicle", "Edit Vehicle")}

            </Button>
            <Button variant="outline">
              <Wrench className="w-4 h-4 mr-2" />{t("maintenance", "Maintenance")}

            </Button>
            <Button className="bg-red-500 hover:bg-red-600 text-white">
              <Navigation className="w-4 h-4 mr-2" />{t("trackLive", "Track Live")}

            </Button>
          </div>
        </div>
      </div>
    </div>);

};

export default VehiclesPage;